/**
 * ShreemTech Universal Admin Panel
 * Copyright (c) 2026 ShreemTech Ltd. All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL -- TRADE SECRET
 */

"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import Link from "next/link";
import { authClient } from "@/lib/auth-client";

const loginSchema = z.object({
  username: z.string().trim().min(1, "Username is required"),
  password: z.string().trim().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const deploymentMode = process.env.NEXT_PUBLIC_DEPLOYMENT_MODE;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setLoading(true);
    try {
      // Always POST to BFF -- the API route handles both standalone and SaaS modes
      // NEXT_PUBLIC_* vars are inlined at build time so cannot gate runtime fetch behaviour
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: data.username, password: data.password }),
        credentials: "same-origin",
      });
      const responseData = await res.json();
      if (!res.ok || !responseData.success) {
        setError(responseData.error || "Invalid credentials");
        return;
      }
      // Fix: set client-readable session cookie so useSessionGuard can find it
      // The API sets HttpOnly cookie (server-readable only), but the client-side
      // session guard reads document.cookie. This bridges the gap.
      authClient.setSession({
        user: responseData.user,
        accessToken: "standalone-" + Date.now(),
        expiresAt: Date.now() + 8 * 60 * 60 * 1000,
      });
      window.location.href = responseData.redirect || "/l2/dashboard";
    } catch (err) {
      const msg = err instanceof Error ? err.message : null;
      setError(msg ?? "Unable to connect. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#1A2742" }}
    >
      <div
        className="w-full max-w-md rounded-xl shadow-lg p-8"
        style={{ background: "#FBF8F1" }}
      >
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold" style={{ color: "#9B7B2C" }}>
            Shreem<span style={{ color: "#3D2D6B" }}>Tech</span>
          </h1>
          <p className="mt-1" style={{ color: "#6b7280", fontSize: "14px" }}>Universal Admin Panel</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "#1A2744" }}>Username</label>
            <input
              {...register("username")}
              type="text"
              placeholder="admin"
              className="w-full px-3 py-2 rounded-md focus:outline-none"
              style={{
                border: "1px solid #E0D8F0",
                fontSize: "14px",
                background: "#fff",
                color: "#1A2744",
              }}
              data-testid="username-input"
            />
            {errors.username && (
              <p className="text-xs mt-1" style={{ color: "#C4483E" }}>{errors.username.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "#1A2744" }}>Password</label>
            <div className="relative">
              <input
                {...register("password")}
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="w-full px-3 py-2 rounded-md focus:outline-none pr-10"
                style={{
                  border: "1px solid #E0D8F0",
                  fontSize: "14px",
                  background: "#fff",
                  color: "#1A2744",
                }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: "#6b7280" }}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="show-password-toggle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs mt-1" style={{ color: "#C4483E" }}>{errors.password.message}</p>
            )}
          </div>

          {error && (
            <div
              className="text-sm px-3 py-2 rounded-md"
              style={{ background: "#FCE8E8", color: "#C4483E" }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 rounded-md font-medium disabled:opacity-50 transition-colors"
            style={{ background: "#3D2D6B", color: "#9B7B2C", fontSize: "14px" }}
            data-testid="login-button"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div className="mt-4 text-center text-sm">
          <Link href="/forgot-password" style={{ color: "#3D2D6B" }} className="hover:underline">
            Forgot password?
          </Link>
        </div>

        {process.env.NEXT_PUBLIC_DEPLOYMENT_MODE !== "saas" && process.env.NEXT_PUBLIC_SHOW_DEFAULT_CREDENTIALS === "true" && (
          <p className="text-xs text-center mt-4" style={{ color: "#6b7280" }} data-testid="standalone-mode-indicator">
            Standalone Mode — Default: admin / Temp@457
          </p>
        )}

        {deploymentMode === "saas" && (
          <div className="text-xs text-center mt-4" style={{ color: "#6b7280" }} data-testid="saas-mode-indicator">
            Connected to ShreemOne SSO
          </div>
        )}

        {process.env.NEXT_PUBLIC_SHOW_DEMO_CREDENTIALS === "true" && (
          <div
            className="mt-6 p-3 rounded-md text-xs"
            style={{ background: "#f0ecf7", color: "#3D2D6B", border: "1px solid #E0D8F0" }}
            data-testid="demo-credentials"
          >
            <p className="font-medium mb-1">Demo credentials:</p>
            <p>Super Admin: admin</p>
            <p>Tenant Admin: tenant-admin</p>
            <p>User: user</p>
            <p className="mt-1 italic">Any password accepted in demo mode.</p>
          </div>
        )}
      </div>
    </div>
  );
}
